/**
 * In-memory holder for the access token + a thin reader for the CSRF
 * double-submit cookie.
 *
 * Access tokens used to live in `localStorage`; any XSS landed them in
 * an attacker's pocket. They now live in a module-scoped variable —
 * lost on page reload but minted again by `/v1/auth/refresh` against
 * the HttpOnly refresh cookie. XSS can read the access token from
 * memory for the duration of its execution, but the *refresh* token
 * (the 7-day artefact) is never JS-visible.
 *
 * The CSRF cookie (`ioa.csrf`) is non-HttpOnly on purpose — the client
 * has to read it to echo it back in the `X-CSRF-Token` header. Its
 * value is meaningless without ALSO presenting the matching HttpOnly
 * refresh cookie, so reading it adds no risk.
 */
const CSRF_COOKIE = 'ioa.csrf';

let accessTokenInMemory: string | null = null;

export const tokenStorage = {
  read(): { accessToken: string | null } {
    return { accessToken: accessTokenInMemory };
  },

  write(tokens: { accessToken: string }): void {
    accessTokenInMemory = tokens.accessToken;
  },

  clear(): void {
    accessTokenInMemory = null;
  },
};

/**
 * Read the CSRF token out of its cookie. Returns null when the cookie
 * is absent (no session, or running outside the browser). Callers
 * should pair this with the bearer header so the server can match it
 * against the HttpOnly refresh cookie.
 */
export function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${CSRF_COOKIE}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return null;
}
